import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import './EmailVerificationPage.css';
import { duracionDeLaPrueba, PRUEBA_TOKENS } from "../limites";
import { enlaceWhatsApp } from '../contacto.js';
import YaEstasDentro from '../components/YaEstasDentro.jsx';

const WHATSAPP_NUMBER = "5215610480746";
const RESEND_COOLDOWN = 60;

// ── WhatsApp SVG Icon ──────────────────────────────────────────────────────────
const WaIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

// ── Floating Orbs ─────────────────────────────────────────────────────────────
function FloatingOrbs() {
    return (
        <div className="evp-orbs" aria-hidden="true">
            <div className="evp-orb evp-orb-1" />
            <div className="evp-orb evp-orb-2" />
            <div className="evp-orb evp-orb-3" />
            <div className="evp-orb evp-orb-4" />
        </div>
    );
}

// ── Animated Particles ────────────────────────────────────────────────────────
function Confetti({ active }) {
    const particles = Array.from({ length: 18 }, (_, i) => ({
        id: i,
        emoji: ['🎉', '✨', '🚀', '⭐', '💎', '🎯', '🔥', '💡'][i % 8],
        left: `${5 + (i * 5.5) % 90}%`,
        delay: `${(i * 150) % 2000}ms`,
        dur: `${1800 + (i * 200) % 1400}ms`,
    }));
    if (!active) return null;
    return (
        <div className="evp-confetti" aria-hidden="true">
            {particles.map(p => (
                <span key={p.id} className="evp-particle" style={{ left: p.left, animationDelay: p.delay, animationDuration: p.dur }}>
                    {p.emoji}
                </span>
            ))}
        </div>
    );
}

// ── Animated Envelope Hero ────────────────────────────────────────────────────
function EnvelopeHero() {
    const [phase, setPhase] = useState('sealed'); // sealed → opening → flying → done

    useEffect(() => {
        const t1 = setTimeout(() => setPhase('opening'), 400);
        const t2 = setTimeout(() => setPhase('flying'), 1100);
        const t3 = setTimeout(() => setPhase('done'), 1900);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }, []);

    return (
        <div className={`evp-envelope-scene evp-phase-${phase}`} aria-hidden="true">
            {/* Glow ring */}
            <div className="evp-glow-ring" />

            {/* Envelope */}
            <div className="evp-envelope">
                {/* Envelope body */}
                <div className="evp-env-body">
                    <div className="evp-env-bottom-left" />
                    <div className="evp-env-bottom-right" />
                    {/* Letter inside */}
                    <div className="evp-env-letter">
                        <div className="evp-letter-logo">S</div>
                        <div className="evp-letter-line" />
                        <div className="evp-letter-line short" />
                    </div>
                    {/* Flap */}
                    <div className="evp-env-flap" />
                </div>
            </div>

            {/* Checkmark (appears after flying) */}
            <div className="evp-check-burst">
                <svg className="evp-checkmark-svg" viewBox="0 0 80 80">
                    <circle className="evp-check-circle" cx="40" cy="40" r="36" />
                    <polyline className="evp-check-line" points="22,40 33,51 58,28" />
                </svg>
                <div className="evp-burst-ring evp-ring-1" />
                <div className="evp-burst-ring evp-ring-2" />
                <div className="evp-burst-ring evp-ring-3" />
            </div>

            {/* Flying sparks */}
            <div className="evp-sparks">
                {[0, 1, 2, 3, 4, 5].map(i => (
                    <div key={i} className={`evp-spark evp-spark-${i}`} />
                ))}
            </div>
        </div>
    );
}

// ── Step Journey Bar ──────────────────────────────────────────────────────────
//  El tercer paso —«Aprender»— no se encendía NUNCA: los tres estaban escritos
//  a mano con «Verificar» siempre activo. La barra prometía un recorrido de
//  tres pasos y se quedaba parada en el segundo, que es justo lo que Rob vio:
//  «tenemos un registro para llegar y validar, pero se queda ahí, sin pasar al
//  siguiente nivel».
function JourneyBar({ verificado = false }) {
    const steps = [
        { icon: '✍️', label: 'Registro', done: true },
        { icon: '📬', label: 'Verificar', done: verificado, active: !verificado },
        { icon: '🚀', label: 'Aprender', done: false, active: verificado },
    ];
    return (
        <div className="evp-journey" role="progressbar" aria-label="Pasos de registro">
            {steps.map((s, i) => (
                <React.Fragment key={s.label}>
                    <div className={`evp-step ${s.done ? 'done' : ''} ${s.active ? 'active' : ''}`}>
                        <div className="evp-step-icon">
                            {s.done ? '✅' : s.icon}
                        </div>
                        <div className="evp-step-label">{s.label}</div>
                    </div>
                    {i < steps.length - 1 && (
                        <div className={`evp-step-line ${s.done ? 'done' : ''}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

// ── Benefit Chip (animated entrance) ─────────────────────────────────────────
function BenefitChip({ emoji, title, desc, color, delay }) {
    return (
        <div className="evp-benefit-chip" style={{ '--chip-accent': color, animationDelay: delay }}>
            <div className="evp-chip-icon" style={{ background: `${color}22`, color }}>
                {emoji}
            </div>
            <div className="evp-chip-body">
                <div className="evp-chip-title">{title}</div>
                <div className="evp-chip-desc">{desc}</div>
            </div>
            <div className="evp-chip-glow" style={{ background: color }} />
        </div>
    );
}

// ── Circular countdown for resend ─────────────────────────────────────────────
function CircularCountdown({ seconds, total }) {
    const radius = 18;
    const circumference = 2 * Math.PI * radius;
    const progress = ((total - seconds) / total) * circumference;
    return (
        <svg className="evp-countdown-ring" width="44" height="44" viewBox="0 0 44 44">
            <circle className="evp-ring-bg" cx="22" cy="22" r={radius} />
            <circle
                className="evp-ring-fill"
                cx="22" cy="22" r={radius}
                strokeDasharray={`${progress} ${circumference}`}
                strokeDashoffset="0"
                transform="rotate(-90 22 22)"
            />
            <text className="evp-ring-text" x="22" y="26" textAnchor="middle" fontSize="11">
                {seconds}s
            </text>
        </svg>
    );
}

// ── Email obfuscator (shows partial email for privacy UX) ─────────────────────
function obfuscateEmail(email) {
    if (!email) return '***@***.com';
    const [user, domain] = email.split('@');
    if (!domain) return email;
    const visible = user.slice(0, Math.min(3, user.length));
    const dots = '•'.repeat(Math.max(2, user.length - 3));
    return `${visible}${dots}@${domain}`;
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function EmailVerificationPage() {
    const location = useLocation();
    const navigate = useNavigate();

    // Get email from router state, localStorage, or fallback
    // El `?email=` de la URL lo pone el servidor cuando alguien abre un enlace
    // de verificación caducado: es el único dato que tenemos de esa persona,
    // porque llega sin sesión.
    const params = new URLSearchParams(location.search);
    const email = params.get('email')
        || location.state?.email
        || JSON.parse(localStorage.getItem('user') || '{}')?.email
        || '';
    const enlaceCaducado = params.get('caducado') === '1';

    // ── EL MISMO SITIO, DE PRINCIPIO A FIN ────────────────────────────────
    //  El servidor manda aquí de vuelta con `?verificado=1` después de activar
    //  la cuenta. Antes mandaba a `/bienvenida`, otra pantalla: el alumno
    //  abría el correo, se le lanzaba una pestaña con algo que no reconocía y
    //  el recorrido que había empezado aquí se quedaba a medias.
    //
    //  `ya=1` es quien vuelve a abrir un enlace que ya había usado. Su cuenta
    //  estaba verificada de antes, así que «acabas de verificar» sería falso.
    const verificado = params.get('verificado') === '1';
    const yaLoHabiaHecho = params.get('ya') === '1';

    // ── ENTRAR SIN VOLVER A ESCRIBIR LA CONTRASEÑA ────────────────────────
    //  Rob, 29-09-2026, tras hacer el alta entera: «lo único que veo es que
    //  tenemos 3 botones de entrada, pero nos manda a login; deberíamos tener
    //  el botón de loguearme con mi nueva cuenta y empezar».
    //
    //  El servidor adjunta un CANJE de 90 segundos y un solo uso. Aquí se
    //  cambia por la sesión de verdad en cuanto carga la pantalla, y con eso
    //  los tres caminos de abajo dejan de pasar por el login — ese componente
    //  ya mira si hay sesión, así que no hay que tocarlo.
    //
    //  Y se limpia de la barra de direcciones en cuanto se usa: un papelito
    //  gastado no tiene por qué quedarse a la vista ni acabar copiado en un
    //  mensaje cuando alguien comparte la dirección.
    const [entrando, setEntrando] = useState(() => !!params.get('canje'));

    useEffect(() => {
        const canje = params.get('canje');
        if (!canje) return undefined;
        let vivo = true;
        (async () => {
            try {
                const base = import.meta.env.VITE_API_URL || '';
                const r = await fetch(`${base}/api/sesion-por-canje`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ canje }),
                });
                if (r.ok) {
                    const d = await r.json();
                    if (d?.access_token) {
                        localStorage.setItem('token', d.access_token);
                        if (d.user) localStorage.setItem('user', JSON.stringify(d.user));
                    }
                }
            } catch { /* si falla, entra por el login de siempre */ }
            if (!vivo) return;
            //  Fuera de la dirección, haya funcionado o no.
            try {
                const limpia = new URL(window.location.href);
                limpia.searchParams.delete('canje');
                window.history.replaceState({}, '', limpia.toString());
            } catch { /* navegador antiguo: se queda, no pasa nada */ }
            setEntrando(false);
        })();
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendStatus, setResendStatus] = useState('idle'); // idle | loading | sent | error
    const [resendCount, setResendCount] = useState(0);
    const [showConfetti, setShowConfetti] = useState(true);

    // Theme sync
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        document.body.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    // Hide confetti after 3s
    useEffect(() => {
        const t = setTimeout(() => setShowConfetti(false), 3200);
        return () => clearTimeout(t);
    }, []);

    // Resend countdown tick
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const handleResend = useCallback(async () => {
        if (resendCooldown > 0 || resendStatus === 'loading') return;
        setResendStatus('loading');
        try {
            const API_BASE_URL = import.meta.env.VITE_API_URL || '';
            const res = await fetch(`${API_BASE_URL}/api/resend-verification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            if (!res.ok) throw new Error('failed');
            setResendStatus('sent');
            setResendCount(c => c + 1);
            setResendCooldown(RESEND_COOLDOWN);
            setTimeout(() => setResendStatus('idle'), 4000);
        } catch {
            // Aquí había un «fallback» que pintaba «✅ ¡Correo enviado!» cuando la
            // llamada fallaba —«if endpoint not implemented»—, y la ruta NO
            // existía en el servidor: el alumno veía que se lo reenviábamos y no
            // se enviaba nada, mientras su cuenta seguía sin poder entrar.
            // Ahora se dice la verdad y queda a la vista el camino de WhatsApp.
            setResendStatus('error');
            setResendCooldown(10);
            setTimeout(() => setResendStatus('idle'), 6000);
        }
    }, [email, resendCooldown, resendStatus]);

    const openWhatsApp = (msg) => {
        window.open(enlaceWhatsApp(msg, WHATSAPP_NUMBER), '_blank');
    };

    const waNoEmail = `¡Hola! Me registré en Spacy pero no recibí el correo de verificación.\n\nMi email: ${email}\n\n¿Pueden ayudarme a activar mi cuenta?`;
    const waSupport = `¡Hola! Tengo problemas para acceder a mi cuenta de Spacy.\n\nMi email: ${email}\n\n¿Me pueden ayudar?`;

    const resendLabel = resendStatus === 'loading'
        ? '⏳ Enviando...'
        : resendStatus === 'sent'
            ? '✅ ¡Correo enviado!'
            : resendStatus === 'error'
                ? '❌ Error — intenta de nuevo'
                : '🔄 Reenviar correo';

    return (
        <div className="evp-root">
            <FloatingOrbs />
            <Confetti active={showConfetti} />

            {/* ── Theme Toggle ── */}
            <button
                className="evp-theme-btn"
                onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
                aria-label="Cambiar tema"
            >
                {theme === 'light' ? '🌙' : '☀️'}
            </button>

            {/* ── Main Card ── */}
            <main className="evp-card">

                {/* Hero Envelope Animation */}
                <EnvelopeHero />

                {/* ── LO ÚNICO QUE ESTA PANTALLA SABE DE VERDAD ──────────────
                    Decía «Correo enviado exitosamente», con su punto verde, y
                    era un texto FIJO: nadie comprobaba nada. El 11-09-2026 se
                    lo dijo a Rob mientras la llave de Resend estaba revocada y
                    no había salido ni un correo.

                    Lo que sí está confirmado cuando se llega aquí es que el
                    servidor creó la cuenta —contestó 200 al registro—. El
                    envío va en segundo plano y esta pantalla no puede verlo,
                    así que no lo afirma. Un check verde sobre algo que no se
                    ha medido convierte un fallo en un misterio: el alumno deja
                    de buscar el problema porque le han dicho que no lo hay. */}
                <div className={`evp-sent-badge ${verificado ? 'evp-sent-badge-ok' : ''}`}>
                    <span className="evp-sent-dot" />
                    {verificado ? 'Correo verificado' : 'Tu cuenta está creada'}
                </div>

                {/* Journey steps */}
                <JourneyBar verificado={verificado} />

                {/* Title & subtitle */}
                {verificado ? (
                    <>
                        <h1 className="evp-title">
                            {yaLoHabiaHecho ? '¡Ya estabas dentro! 🚀' : '¡Ya estás dentro! 🚀'}
                        </h1>
                        <p className="evp-subtitle">
                            {yaLoHabiaHecho
                                ? <>Ese enlace ya lo habías usado: <strong className="evp-email-highlight">{email}</strong> lleva verificado desde antes. No tienes que hacer nada más.</>
                                : <>Verificamos <strong className="evp-email-highlight">{email}</strong>. Tu cuenta está lista.</>}
                        </p>
                    </>
                ) : (
                    <>
                        <h1 className="evp-title">¡Ya casi estás dentro! 🚀</h1>
                        <p className="evp-subtitle">
                            Enviamos un enlace de verificación a{' '}
                            <strong className="evp-email-highlight">{obfuscateEmail(email)}</strong>
                            .<br />
                            Revisa tu bandeja de entrada — y también el <span className="evp-spam-tag">📁 spam</span>, por si acaso.
                        </p>
                    </>
                )}

                {/* ── YA VERIFICADO: aquí se acaba el recorrido, no en otra
                       pantalla. Lo que tiene, por dónde empieza y qué puede
                       pedir, debajo de la misma barra de pasos que lleva
                       mirando desde que se registró. */}
                {verificado && !entrando && <YaEstasDentro correo={email} />}

                {/* ── Freemium Benefits ──
                       «Lo que te espera al verificar» solo tiene sentido ANTES
                       de verificar. Después, lo que tiene se lo cuenta
                       `YaEstasDentro` en presente y con los números buenos;
                       dejar las dos cosas sería decirle lo mismo dos veces, y
                       una de ellas en futuro cuando ya es suyo. */}
                {!verificado && (
                    <div className="evp-benefits-section">
                        <div className="evp-benefits-header">
                            <span className="evp-benefits-eye">🎁</span>
                            <span>Lo que te espera al verificar</span>
                        </div>
                        <div className="evp-benefits-grid">
                            <BenefitChip emoji="📅" title={`${duracionDeLaPrueba()} gratis`} desc="Acceso Premium completo" color="#6366f1" delay="0.6s" />
                            <BenefitChip emoji="🤖" title={`${PRUEBA_TOKENS} tokens IA`} desc="Para tu Tutor Spacy" color="#00CFFF" delay="0.75s" />
                            <BenefitChip emoji="🧠" title="Tutor IA 24/7" desc="Aprende a tu ritmo" color="#a855f7" delay="0.9s" />
                            <BenefitChip emoji="🎮" title="Minigames" desc="Aprende jugando" color="#f472b6" delay="1.05s" />
                        </div>
                    </div>
                )}

                {/* ── TODO ESTO ES PARA QUIEN AÚN ESPERA EL CORREO ──────────
                       Reenviar, «¿no llegó?» y los consejos de spam no le
                       sirven de nada a quien ya verificó: su correo llegó y ya
                       lo usó. Dejarlo puesto le ofrecería reparar algo que
                       funciona —y el botón de reenviar, además, no le mandaría
                       nada: el servidor contesta 422 a una cuenta verificada.
                       Eso es exactamente el callejón del que Rob no podía
                       salir. */}
                {!verificado && (
                  <>
                {/* ── Divider ── */}
                <div className="evp-divider-full" />

                {/* ── "No llegó el correo" section ── */}
                <div className="evp-no-email-section">
                    {/* El enlace dura 48 horas. Quien abre uno viejo llega aquí,
                        y lo que necesita es pedir otro sin tener que entenderlo. */}
                    {enlaceCaducado && (
                        <p className="evp-no-email-q" style={{ color: 'var(--wrn, #f5a623)' }}>
                            ⏳ Ese enlace ya caducó. Pídenos uno nuevo aquí abajo.
                        </p>
                    )}
                    <p className="evp-no-email-q">¿No llegó el correo? 🤔</p>

                    {/* Resend button */}
                    <div className="evp-resend-row">
                        <button
                            className={`evp-resend-btn evp-status-${resendStatus}`}
                            onClick={handleResend}
                            disabled={resendCooldown > 0 || resendStatus === 'loading'}
                        >
                            {resendCooldown > 0 ? (
                                <CircularCountdown seconds={resendCooldown} total={RESEND_COOLDOWN} />
                            ) : null}
                            <span>{resendLabel}</span>
                            {resendCooldown > 0 && (
                                <span className="evp-resend-hint">Puedes reenviar en {resendCooldown}s</span>
                            )}
                        </button>
                        {resendCount > 1 && (
                            <p className="evp-resend-tip">
                                💡 Si ya intentaste {resendCount} veces, lo mejor es activarlo por WhatsApp.
                            </p>
                        )}
                    </div>

                    {/* Or divider */}
                    <div className="evp-or-divider">
                        <span className="evp-or-line" />
                        <span className="evp-or-text">o actívalo en segundos</span>
                        <span className="evp-or-line" />
                    </div>

                    {/* WhatsApp CTA — Primary */}
                    <button className="evp-wa-primary" onClick={() => openWhatsApp(waNoEmail)}>
                        <WaIcon />
                        <span>Activar cuenta por WhatsApp</span>
                        <span className="evp-wa-badge">⚡ menos de 1 hora</span>
                    </button>

                    <p className="evp-wa-desc">
                        Un asesor te activará el acceso manualmente. Sin esperas, sin formularios.
                    </p>
                </div>

                {/* ── Tips accordion-style ── */}
                <details className="evp-tips">
                    <summary className="evp-tips-toggle">💡 ¿Por qué no llegó mi correo?</summary>
                    <ul className="evp-tips-list">
                        <li>📁 Revisa la carpeta <strong>Spam</strong> o <strong>Promociones</strong></li>
                        <li>⏱ Puede tardar hasta <strong>5 minutos</strong> en llegar</li>
                        <li>✉️ Verifica que el correo esté bien escrito al registrarte</li>
                        <li>🏢 Si usas correo corporativo, puede que tu IT lo bloquee</li>
                    </ul>
                </details>
                  </>
                )}

            </main>

            {/* ── Support Footer ── */}
            <footer className="evp-support-footer">
                <div className="evp-support-card">
                    <div className="evp-support-icon">🛟</div>
                    <div className="evp-support-copy">
                        <strong>¿Problemas con tu acceso?</strong>
                        <span>Soporte disponible de lunes a sábado</span>
                    </div>
                    <button className="evp-wa-ghost" onClick={() => openWhatsApp(waSupport)}>
                        <WaIcon />
                        Contactar
                    </button>
                </div>

                <div className="evp-footer-links">
                    <Link to="/login" className="evp-footer-link">← Volver al login</Link>
                    <span className="evp-footer-sep">·</span>
                    <Link to="/register" className="evp-footer-link">Corregir correo</Link>
                </div>
            </footer>
        </div>
    );
}